import { useState, useEffect, useRef, useCallback } from 'react';
import { apiGet } from '../api/apiClient';
import type { TraderProfile } from './traderProfileTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ChartMode = 'pnl' | 'portfolio' | 'balance';

// Returned by ?type=pnl or ?type=portfolio
export interface PnlPoint {
    ts: string; // ISO datetime
    total_pnl: number;
    portfolio_value: number | null;
}

// Returned by ?type=balance
export interface BalancePoint {
    ts: string;
    balance_usd: number;
}

// Returned by ?type=flows
export interface FlowPoint {
    ts: string;
    inflow_usd: number;
    outflow_usd: number;
    buy_volume_usd: number;
    sell_volume_usd: number;
    deposit_usd: number;
    withdraw_usd: number;
}

export type FlowMode = 'deposit_withdraw' | 'buy_sell' | 'inflow_outflow';

export type PrimaryPoint = PnlPoint | BalancePoint;

interface SeriesResponse<T> {
    series_type: string;
    points: T[];
}

// ---------------------------------------------------------------------------
// useTraderProfile
// ---------------------------------------------------------------------------

export function useTraderProfile(address: string | undefined): {
    data: TraderProfile | null;
    loading: boolean;
    error: string | null;
} {
    const [data, setData] = useState<TraderProfile | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const callIdRef = useRef(0);

    useEffect(() => {
        if (!address) return;

        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setData(null);

        apiGet<TraderProfile>(`/api/trader/${address}`, controller.signal)
            .then(res => {
                if (callId !== callIdRef.current) return;
                setData(res);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current)
                    return;
                console.error('trader-profile failed:', err);
                setError(err instanceof Error ? err.message : String(err));
                setData(null);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });

        return () => controller.abort();
    }, [address]);

    return { data, loading, error };
}

// ---------------------------------------------------------------------------
// Internal generic hook
// ---------------------------------------------------------------------------

function useSeriesInternal<T>(
    address: string | undefined,
    type: string,
    enabled: boolean
): {
    data: T[] | null;
    loading: boolean;
    error: string | null;
} {
    const [data, setData] = useState<T[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const callIdRef = useRef(0);

    // Reset when address changes
    useEffect(() => {
        setData(null);
        setError(null);
    }, [address]);

    useEffect(() => {
        if (!address || !enabled) return;

        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        apiGet<SeriesResponse<T>>(
            `/api/trader/${address}/series?type=${type}`,
            controller.signal
        )
            .then(res => {
                if (callId !== callIdRef.current) return;
                setData(res.points);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current)
                    return;
                console.error(`trader-series (${type}) failed:`, err);
                setError(err instanceof Error ? err.message : String(err));
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });

        return () => controller.abort();
    }, [address, type, enabled]);

    return { data, loading, error };
}

// ---------------------------------------------------------------------------
// Public hooks — one per primary series type
// ---------------------------------------------------------------------------

/** PnL series — fires on mount, always on */
export function useTraderPnlSeries(address: string | undefined) {
    return useSeriesInternal<PnlPoint>(address, 'pnl', true);
}

/** Portfolio-value series — always on */
export function useTraderPortfolioSeries(address: string | undefined) {
    // Both pnl and portfolio return PnlPoint (same query, same shape)
    return useSeriesInternal<PnlPoint>(address, 'portfolio', true);
}

/** USDC balance series — always on */
export function useTraderBalanceSeries(address: string | undefined) {
    return useSeriesInternal<BalancePoint>(address, 'balance', true);
}

// ---------------------------------------------------------------------------
// computeGlobalDomain
// ---------------------------------------------------------------------------

/**
 * Returns the [min, max] Date extent across all provided series, ignoring
 * future timestamps. Pass into TraderPnlChart as globalDomain so the x-axis
 * stays stable when switching between chart modes.
 */
export function computeGlobalDomain(
    pnlPoints: PnlPoint[] | null,
    portfolioPoints: PnlPoint[] | null,
    balancePoints: BalancePoint[] | null,
    flowPoints?: FlowPoint[] | null
): [Date, Date] | null {
    const now = Date.now();
    let min = Infinity;
    let max = -Infinity;

    const check = (ts: string) => {
        const t = new Date(ts).getTime();
        if (t > now) return;
        if (t < min) min = t;
        if (t > max) max = t;
    };

    for (const p of pnlPoints ?? []) check(p.ts);
    for (const p of portfolioPoints ?? []) check(p.ts);
    for (const p of balancePoints ?? []) check(p.ts);
    for (const p of flowPoints ?? []) check(p.ts);

    if (!isFinite(min)) return null;
    return [new Date(min), new Date(max)];
}

/** Flow series — fires on mount, shared across all chart modes */
export function useTraderFlowSeries(address: string | undefined) {
    return useSeriesInternal<FlowPoint>(address, 'flows', true);
}

// ---------------------------------------------------------------------------
// useSimilarTraders
// ---------------------------------------------------------------------------
// v2: the backing table now stores one embedding per trader instead of four,
// so `method`/weights and the per-category sim_* scores are gone in favor of
// a single `similarity` score.

export interface SimilarTrader {
    address: string;
    tags: string[];
    // Why the Data Error tag is set; missing on an older indexer.
    data_issues?: string[];
    similarity: number;
    portfolio_value: number;
    total_pnl: number;
    win_rate: number;
    avg_edge: number;
    num_trades: number;
    num_markets: number;
}

export interface SimilarTradersResponse {
    traders: SimilarTrader[];
}

export function useSimilarTraders(
    address: string | undefined,
    limit: number
): {
    data: SimilarTrader[] | null;
    loading: boolean;
    error: string | null;
} {
    const [data, setData] = useState<SimilarTrader[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const callIdRef = useRef(0);

    useEffect(() => {
        if (!address) return;

        const params = new URLSearchParams({
            limit: String(limit),
        });

        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        apiGet<SimilarTradersResponse>(
            `/api/trader/${address}/similar?${params}`,
            controller.signal
        )
            .then(res => {
                if (callId !== callIdRef.current) return;
                setData(res.traders);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current)
                    return;
                console.error('similar-traders failed:', err);
                setError(err instanceof Error ? err.message : String(err));
                setData(null);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });

        return () => controller.abort();
    }, [address, limit]);

    return { data, loading, error };
}
